import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, TextField, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { decryptData } from "../../Services/TabServices";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Toast } from "../Common/Toast";

const OTP_LENGTH = 6;

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

// Sequential validation result type
interface SequentialResult {
  hasSequential: boolean;
  matchedPattern: string | null;
}

// Password strength state type
interface PasswordStrength {
  hasLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
  noSequential: boolean;
  matchedSequentialPattern: string | null;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  open,
  onClose,
}) => {
    const { executeRecaptcha } = useGoogleReCaptcha();

  const [step, setStep] = useState<"identifier" | "otp" | "reset">("identifier");
  const [phoneNumberEmail, setPhoneNumberEmail] = useState("");
  const [userId, setUserId] = useState<number>(0);
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [otpTimer, setOtpTimer] = useState(600);
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  
  // Password strength state
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    hasLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
    noSequential: true,
    matchedSequentialPattern: null,
  });

  const otpRefs = useRef<(HTMLInputElement | null)[]>(Array(OTP_LENGTH).fill(null));
  const otpExpiryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const resendTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (otpExpiryTimerRef.current) clearInterval(otpExpiryTimerRef.current);
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (step === "otp" && otpTimer > 0) {
      otpExpiryTimerRef.current = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            if (otpExpiryTimerRef.current) clearInterval(otpExpiryTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => {
        if (otpExpiryTimerRef.current) clearInterval(otpExpiryTimerRef.current);
      };
    }
  }, [step]);

  useEffect(() => {
    if (step === "otp" && resendTimer > 0 && !canResend) {
      resendTimerRef.current = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            if (resendTimerRef.current) clearInterval(resendTimerRef.current);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => {
        if (resendTimerRef.current) clearInterval(resendTimerRef.current);
      };
    }
  }, [step, canResend]);

  // Sequential pattern detection function
  const hasSequentialChars = (password: string): SequentialResult => {
    const sequentialPatterns = [
      "0123", "1234", "2345", "3456", "4567", "5678", "6789",
      "3210", "4321", "5432", "6543", "7654", "8765", "9876",
      "123", "234", "345", "456", "567", "678", "789",
      "321", "432", "543", "654", "765", "876", "987",
      "abcd", "bcde", "cdef", "defg",
      "dcba", "edcb", "fedc",
      "abc", "bcd", "cde",
      "cba", "dcb", "edc",
      "password", "password123", "password@123",
      "admin", "admin123",
      "welcome", "welcome123",
      "letmein", "login", "user", "guest",
      "123456", "123456789", "111111", "000000",
      "112233", "121212",
      "pass", "word", "test", "hello",
    ];

    const lowerPassword = password.toLowerCase();

    for (const pattern of sequentialPatterns) {
      if (lowerPassword.includes(pattern.toLowerCase())) {
        return { hasSequential: true, matchedPattern: pattern };
      }
    }

    return { hasSequential: false, matchedPattern: null };
  };

  // Password strength validation function
  const validatePasswordStrength = (password: string): PasswordStrength => {
    const sequentialResult = hasSequentialChars(password);

    return {
      hasLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[@$!%*?&]/.test(password),
      noSequential: !sequentialResult.hasSequential,
      matchedSequentialPattern: sequentialResult.matchedPattern,
    };
  };

  const handleVerifyOtp = useCallback(async () => {
    const otpString = otp.join("");
    if (otpString.length !== OTP_LENGTH) {
      setErrorMessage("Please enter complete OTP");
      return;
    }
    try {
      setIsLoading(true);
      setErrorMessage("");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Auth/VerifyForgotPasswordOTP`,
        { userId: userId, enteredOtp: otpString }
      );
      if (response.data.success) {
        setSuccessMessage("OTP verified successfully!");
        setStep("reset");
      } else {
        setErrorMessage(response.data.message || "Invalid OTP");
        setOtp(Array(OTP_LENGTH).fill(""));
        otpRefs.current[0]?.focus();
      }
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message || "OTP verification failed"
      );
      setOtp(Array(OTP_LENGTH).fill(""));
      otpRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
         setTimeout(() => {
       setSuccessMessage("")
              setErrorMessage("")

      }, 2000);
    }
  }, [otp, userId]);

  useEffect(() => {
    const otpString = otp.join("");
    if (otpString.length === OTP_LENGTH && step === "otp" && !isLoading) {
      const timer = setTimeout(() => handleVerifyOtp(), 300);
      return () => clearTimeout(timer);
    }
  }, [otp.join(""), step, isLoading, handleVerifyOtp]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getLastFourDigits = (value: string): string => {
    return value.slice(-4);
  };

  const handleSendOtp = async () => {
//  if (!executeRecaptcha) {
//       Toast.error('reCAPTCHA not ready. Please try again.');
//       // setLoading(false);
//       return;
//     }
  

    // ✅ FIX 3: Call executeRecaptcha ONCE (removed duplicate)
  //  const recaptchaToken = await executeRecaptcha('callback_request');

  //   if (!recaptchaToken) {
  //       Toast.error('Captcha verification failed. Please try again.');
  //     // setMessage('Captcha verification failed. Please try again.');
  //     // setLoading(false);
  //        return;
  //   }
  
    try {
      setIsLoading(true);
      setErrorMessage("");
      if (!phoneNumberEmail.trim()){
        setErrorMessage("Please enter phone number or email");
        return;
      }
      //  console.log("hey3")
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Auth/ForgotPasswordOTP`,
        { phoneNumberEmail
          // ,recaptchaToken
        },
  {
    headers: {
      "Content-Type": "application/json",
    },
  }
    );

      if (response.data.message) {
        setSuccessMessage(response.data.message);
        const userId = decryptData(response.data.userId);
        setUserId(parseInt(userId));
        setStep("otp");
        setOtpTimer(600);
        setResendTimer(30);
        setCanResend(false);
        setOtp(Array(OTP_LENGTH).fill(""));
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      }
    } catch (error: any) {
      console.error("ForgotPasswordOTP ERROR:", error.response?.data);
      setErrorMessage(error?.response?.data?.message || "Failed to send OTP.");
    } finally {
      setIsLoading(false);
         setTimeout(() => {
       setSuccessMessage("")
              setErrorMessage("")
      }, 2000);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const digitOnly = value.replace(/\D/g, "").slice(0, 1);

    setOtp((prev) => {
      const newOtp = [...prev];
      newOtp[index] = digitOnly;
      return newOtp;
    });

    setErrorMessage("");

    if (digitOnly && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        setOtp((prev) => {
          const newOtp = [...prev];
          newOtp[index - 1] = "";
          return newOtp;
        });
        otpRefs.current[index - 1]?.focus();
      } else {
        setOtp((prev) => {
          const newOtp = [...prev];
          newOtp[index] = "";
          return newOtp;
        });
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleResetPassword = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");

      if (!newPassword || !confirmPassword) {
        setErrorMessage("Please fill all fields");
        return;
      }

      // Check all password strength criteria
      if (!Object.entries(passwordStrength).every(([key, value]) => value === true || key === 'matchedSequentialPattern')) {
        setErrorMessage("Password must meet all security requirements");
        return;
      }

      if (newPassword !== confirmPassword) {
        setErrorMessage("Passwords do not match");
        return;
      }

     const response = await axios.patch(
  `${process.env.REACT_APP_BASE_URL}/api/Auth/UpdatePassword?userId=${userId}&newPassword=${newPassword}`
);

      setSuccessMessage("Password reset successfully!");
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.message || "Password reset failed"
      );
    } finally {
      setIsLoading(false);
         setTimeout(() => {
       setSuccessMessage("")
              setErrorMessage("")

      }, 2000);
    }
  };

  const handleResendOtp = async () => {
    setOtp(Array(OTP_LENGTH).fill(""));
    setCanResend(false);
    setResendTimer(30);
    await handleSendOtp();
  };

  const handleClose = () => {
    if (otpExpiryTimerRef.current) clearInterval(otpExpiryTimerRef.current);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);

    setStep("identifier");
    setPhoneNumberEmail("");
    setUserId(0);
    setOtp(Array(OTP_LENGTH).fill(""));
    setNewPassword("");
    setConfirmPassword("");
    setErrorMessage("");
    setSuccessMessage("");
    setOtpTimer(600);
    setResendTimer(30);
    setCanResend(false);
    setPasswordStrength({
      hasLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecial: false,
      noSequential: true,
      matchedSequentialPattern: null,
    });
    onClose();
  };

  // Check if password is strong enough
  const isPasswordStrong = Object.entries(passwordStrength).every(
    ([key, value]) => key === 'matchedSequentialPattern' || value === true
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="forgot-password-modal"
    >
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white shadow-2xl rounded-2xl outline-none w-[92%] md:w-[480px] max-h-[85vh] overflow-y-auto">
        {/* Close Button */}
        <div className="sticky top-0 bg-white z-10 flex justify-end px-4 pb-0">
          <IconButton
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <CloseIcon />
          </IconButton>
        </div>

        <div className="px-6 pb-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {step === "identifier" && "Forgot Password"}
              {step === "reset" && "Reset Password"}
            </h2>
            <p className="text-gray-600 text-sm">
              {step === "identifier" &&
                "Enter your phone number or email to receive OTP"}
              {step === "reset" && "Create a new password for your account"}
            </p>
          </div>

          {/* Step 1: Enter Phone/Email */}
          {step === "identifier" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendOtp();
              }}
              className="space-y-4"
            >
              <TextField
                required
                label="Phone Number or Email"
                name="phoneNumberEmail"
                value={phoneNumberEmail}
                fullWidth
                size="small"
                onChange={(e) => setPhoneNumberEmail(e.target.value)}
                sx={{
                  "& .MuiInputBase-root": {
                    height: "52px",
                    borderRadius: "12px",
                  },
                }}
              />

              <button
                type="submit"
                disabled={isLoading || !phoneNumberEmail}
                className={`w-full py-4 rounded-2xl text-white font-semibold transition-all ${
                  isLoading || !phoneNumberEmail
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <svg
                      className="w-5 h-5 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      />
                    </svg>
                    <span>Sending OTP...</span>
                  </div>
                ) : (
                  "Send OTP"
                )}
              </button>
            </form>
          )}

          {/* Step 2: Verify OTP */}
          {step === "otp" && (
            <>
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-[#21808d] to-[#009A85] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Let's confirm it's you!
                </h3>
                <p className="text-gray-500 text-xs leading-relaxed mb-1">
                  Your OTP is on the way!
                </p>
                <p className="text-gray-500 text-xs leading-relaxed">
                  Look for the OTP sent to *****
                  <strong>{getLastFourDigits(phoneNumberEmail)}</strong> and
                  verify to proceed.
                </p>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleVerifyOtp();
                }}
                autoComplete="off"
                className="space-y-2"
              >
                <div className="flex justify-center gap-3">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        otpRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className="w-10 h-10 text-center text-sm font-bold border-2 rounded-xl focus:border-teal-500 focus:ring-4 ring-teal-100 outline-none transition-all bg-white shadow-sm hover:shadow-md"
                    />
                  ))}
                </div>
                <div className="text-start">
                  <p className="text-xs text-gray-600">
                    OTP expires in:{" "}
                    <span className="font-semibold text-teal-600">
                      {formatTime(otpTimer)}
                    </span>
                  </p>
                </div>

                <div className="text-start">
                  {canResend ? (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={isLoading}
                      className="text-teal-600 hover:text-teal-700 text-xs px-4 py-1 border border-teal-200 rounded-xl hover:bg-teal-50 transition-all"
                    >
                      Resend OTP
                    </button>
                  ) : (
                    <p className="text-sm text-gray-500">
                      Resend OTP in{" "}
                      <span className="font-semibold text-teal-600">
                        {resendTimer}s
                      </span>
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || otp.join("").length !== OTP_LENGTH}
                  className={`w-full py-4 rounded-2xl text-white font-semibold text-lg shadow-lg transition-all duration-300 ${
                    isLoading || otp.join("").length !== OTP_LENGTH
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 active:scale-95 hover:shadow-xl"
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center space-x-3">
                      <svg
                        className="w-6 h-6 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"
                        />
                      </svg>
                      <span>Verifying...</span>
                    </div>
                  ) : (
                    "Verify OTP"
                  )}
                </button>
              </form>
            </>
          )}

          {/* Step 3: Reset Password with Strength Indicator */}
          {step === "reset" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleResetPassword();
              }}
              className="space-y-2"
            >
              {/* New Password Field */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  New Password *
                </label>
                <div className="relative">
                  <TextField
                    required
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    fullWidth
                    size="small"
                    disabled={isLoading}
                    inputProps={{ maxLength: 15 }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewPassword(value);
                      setPasswordStrength(validatePasswordStrength(value));
                    }}
                    placeholder="Enter new password"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "52px",
                        borderRadius: "12px",
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                            disabled={isLoading}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </div>

              {/* Password Strength Indicator */}
              {newPassword && (
                <div className="mt-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                  <p className="text-sm font-semibold text-gray-700 mb-3">
                    Password must include:
                  </p>
                  <div className="space-y-2 text-xs">
                    <div
                      className={`flex items-center ${
                        passwordStrength.hasLength
                          ? "text-green-600"
                          : "text-gray-500"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full flex items-center justify-center mr-3 ${
                          passwordStrength.hasLength
                            ? "bg-green-500 text-white"
                            : "bg-gray-300"
                        }`}
                      >
                        {passwordStrength.hasLength ? "✓" : "○"}
                      </div>
                      At least 8 characters
                    </div>
                    <div
                      className={`flex items-center ${
                        passwordStrength.hasUppercase
                          ? "text-green-600"
                          : "text-gray-500"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full flex items-center justify-center mr-3 ${
                          passwordStrength.hasUppercase
                            ? "bg-green-500 text-white"
                            : "bg-gray-300"
                        }`}
                      >
                        {passwordStrength.hasUppercase ? "✓" : "○"}
                      </div>
                      1 uppercase letter (A-Z)
                    </div>
                    <div
                      className={`flex items-center ${
                        passwordStrength.hasLowercase
                          ? "text-green-600"
                          : "text-gray-500"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full flex items-center justify-center mr-3 ${
                          passwordStrength.hasLowercase
                            ? "bg-green-500 text-white"
                            : "bg-gray-300"
                        }`}
                      >
                        {passwordStrength.hasLowercase ? "✓" : "○"}
                      </div>
                      1 lowercase letter (a-z)
                    </div>
                    <div
                      className={`flex items-center ${
                        passwordStrength.hasNumber
                          ? "text-green-600"
                          : "text-gray-500"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full flex items-center justify-center mr-3 ${
                          passwordStrength.hasNumber
                            ? "bg-green-500 text-white"
                            : "bg-gray-300"
                        }`}
                      >
                        {passwordStrength.hasNumber ? "✓" : "○"}
                      </div>
                      1 number (0-9)
                    </div>
                    <div
                      className={`flex items-center ${
                        passwordStrength.hasSpecial
                          ? "text-green-600"
                          : "text-gray-500"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full flex items-center justify-center mr-3 ${
                          passwordStrength.hasSpecial
                            ? "bg-green-500 text-white"
                            : "bg-gray-300"
                        }`}
                      >
                        {passwordStrength.hasSpecial ? "✓" : "○"}
                      </div>
                      1 special character (@$!%*?&)
                    </div>
                    <div className="flex items-center">
                      <div
                        className={`w-4 h-4 rounded-full flex items-center justify-center mr-3 ${
                          passwordStrength.noSequential
                            ? "bg-green-500 text-white"
                            : "bg-red-500 text-white"
                        }`}
                      >
                        {passwordStrength.noSequential ? "✓" : "✗"}
                      </div>
                      <div
                        className={
                          passwordStrength.noSequential
                            ? "text-green-600"
                            : "text-red-500"
                        }
                      >
                        No sequential characters{" "}
                        {passwordStrength.matchedSequentialPattern && (
                          <span className="font-mono bg-red-100 px-1 py-0.5 rounded text-xs text-red-700">
                            ({passwordStrength.matchedSequentialPattern})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`mt-2 p-2 rounded text-xs font-medium text-center ${
                      isPasswordStrong
                        ? "bg-green-100 text-green-800 border border-green-200"
                        : "bg-yellow-100 text-yellow-800 border border-yellow-200"
                    }`}
                  >
                    {isPasswordStrong
                      ? "✅ Strong password!"
                      : "⚠️ Fill all requirements above"}
                  </div>
                </div>
              )}

              {/* Confirm Password Field */}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700">
                  Confirm Password *
                </label>
                <div className="relative">
                  <TextField
                    required
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    fullWidth
                    size="small"
                    disabled={isLoading}
                    inputProps={{ maxLength: 15 }}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "52px",
                        borderRadius: "12px",
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword((prev) => !prev)
                            }
                            edge="end"
                            disabled={isLoading}
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={
                  isLoading ||
                  !newPassword ||
                  !confirmPassword ||
                  !isPasswordStrong
                }
                className={`w-full py-4 rounded-2xl text-white font-semibold transition-all ${
                  isLoading ||
                  !newPassword ||
                  !confirmPassword ||
                  !isPasswordStrong
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <svg
                      className="w-5 h-5 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      />
                    </svg>
                    <span>Resetting Password...</span>
                  </div>
                ) : (
                  "Reset Password"
                )}
              </button>
            </form>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 border-l-4 border-green-400 p-1 rounded-lg mt-2">
              <p className="text-green-700 text-xs font-medium">
                {successMessage}
              </p>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="bg-red-50 border-l-4 border-red-400 p-1 rounded-lg mt-2">
              <p className="text-red-700 text-xs font-medium">{errorMessage}</p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ForgotPasswordModal;
